import { useState } from 'react'
import { AlertTriangle, Building2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCreateOrg } from '../api/auth'

export function OnboardingPage() {
  const { t } = useTranslation('auth')
  const createOrg = useCreateOrg()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await createOrg.mutateAsync({
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
      })
      // Backend has already set active_org_id on the session for session-auth
      // users (vapi-voice-agent/src/auth/routes.py:118-120). Top-level
      // navigation drops us into the dashboard with the new org active.
      window.location.assign('/')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div className="min-h-screen bg-background text-text-primary">
      <header className="border-b border-border bg-surface/60">
        <div className="mx-auto flex max-w-2xl items-center justify-center px-6 py-5">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-background">
              V
            </span>
            Voysi AI
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-10">
        <form
          onSubmit={onSubmit}
          className="space-y-6 rounded-xl border border-border bg-surface p-6 shadow-lg"
        >
          <div className="flex items-center gap-4 border-b border-border pb-5">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-surface-elevated text-primary">
              <Building2 className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold">
                {t('onboarding.title')}
              </h1>
              <p className="text-sm text-text-secondary">
                {t('onboarding.subtitle')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary">
                {t('onboarding.nameLabel')}
              </label>
              <input
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('onboarding.namePlaceholder')}
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary">
                {t('onboarding.slugLabel')}
              </label>
              <input
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder={t('onboarding.slugPlaceholder')}
                pattern="^[a-z0-9-]+$"
                required
              />
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-3 rounded-lg border border-error/50 bg-error/10 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-error" />
              <div className="text-sm text-text-secondary">{error}</div>
            </div>
          )}

          <div className="flex justify-end border-t border-border pt-5">
            <button
              type="submit"
              disabled={createOrg.isPending}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-background hover:bg-primary-hover disabled:opacity-50"
            >
              {createOrg.isPending
                ? t('onboarding.creating')
                : t('onboarding.cta')}
            </button>
          </div>
        </form>
      </main>
    </div>
  )
}
